import React from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { useApp } from '@/context/AppContext';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Brain, Target, Activity, Trophy, Award, Sparkles, BarChart3, ClipboardCheck,
  ArrowRight, GraduationCap, Medal, Search,
} from 'lucide-react';

const TOOLS = [
  {
    id: 'assess',
    to: '/academy/talent-assessment',
    icon: Brain,
    title_ar: 'تقييم المواهب والتوجيه',
    title_en: 'Talent Assessment & Guidance',
    desc_ar: 'استبيان ذكي يحدّد قدراتك الحركية والذهنية ويوصي بأنسب رياضة لك.',
    desc_en: 'A smart questionnaire that maps your motor & cognitive abilities to the best-fit sport.',
    accent: 'from-amber-400 to-orange-500',
    badge_ar: 'لجميع المتقدّمين',
    badge_en: 'For all applicants',
  },
  {
    id: 'training',
    to: '/academy/training',
    icon: Target,
    title_ar: 'مناهج التدريب الذكية',
    title_en: 'Smart Training Curricula',
    desc_ar: 'مكتبة برامج تدريبية + مولّد ذكاء اصطناعي لخطط تدريب شخصية.',
    desc_en: 'A library of training programs + an AI generator for personalised plans.',
    accent: 'from-emerald-500 to-teal-600',
    badge_ar: 'للمدرّبين والرياضيين',
    badge_en: 'Coaches & athletes',
  },
  {
    id: 'classify',
    to: '/academy/classification',
    icon: Trophy,
    title_ar: 'التصنيف الفني البارالمبي',
    title_en: 'Paralympic Functional Classification',
    desc_ar: 'تحليل حركي بالذكاء الاصطناعي يقترح فئتك التصنيفية الدولية (IPC) ويصدر شهادة.',
    desc_en: 'AI motion analysis suggests your IPC class and issues a certificate.',
    accent: 'from-sky-500 to-blue-700',
    badge_ar: 'رياضات بارالمبية',
    badge_en: 'Paralympic only',
  },
];

const STATS = [
  { ar: 'فئات IPC مدعومة', en: 'IPC classes', value: '15+', icon: Medal },
  { ar: 'رياضة في الكتالوج', en: 'Sports', value: '13', icon: Trophy },
  { ar: 'مولّد خطّة بالذكاء', en: 'AI plan generator', value: 'AI', icon: Sparkles },
  { ar: 'تقرير حركي تلقائي', en: 'Motion report', value: 'auto', icon: Activity },
];

export default function AcademyHub() {
  const { academy } = useParams();
  const { lang } = useApp();
  const isAr = lang === 'ar';
  if (!['paralympic', 'special-olympics'].includes(academy)) {
    return <Navigate to="/" replace />;
  }
  const hue = academy === 'paralympic' ? 210 : 285;
  const title_ar = academy === 'paralympic'
    ? 'مركز الأكاديمية البارالمبية الذكي'
    : 'مركز أكاديمية الأولمبياد الخاص الذكي';
  const title_en = academy === 'paralympic'
    ? 'Paralympic Academy Smart Center'
    : 'Special Olympics Smart Center';

  return (
    <main id="main" className="bg-gradient-to-b from-white via-stone-50 to-white min-h-screen" data-testid={`academy-hub-${academy}`}>
      {/* Header */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
          <div className="absolute -top-32 -end-32 w-96 h-96 rounded-full blur-3xl" style={{ background: `hsla(${hue}, 80%, 60%, 0.18)` }} />
          <div className="absolute -bottom-32 -start-32 w-96 h-96 rounded-full blur-3xl" style={{ background: `hsla(${hue + 60}, 80%, 60%, 0.18)` }} />
        </div>
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          <Badge className="mb-4 text-xs px-3 py-1 font-bold" style={{ background: `hsl(${hue}, 70%, 35%)`, color: 'white' }}>
            <Sparkles className="w-3 h-3 me-1" />{isAr ? 'مركز ذكي متكامل' : 'Integrated smart center'}
          </Badge>
          <h1 className="text-3xl sm:text-5xl font-black leading-tight mb-3 max-w-3xl" style={{ color: `hsl(${hue}, 70%, 25%)` }}>
            {isAr ? title_ar : title_en}
          </h1>
          <p className="text-base sm:text-lg text-muted-foreground max-w-3xl leading-relaxed">
            {isAr
              ? 'ثلاث أدوات ذكية متكاملة لاكتشاف الموهبة، توجيهها للرياضة الأنسب، وبناء خطط تدريب وتصنيف فني احترافي.'
              : 'Three integrated smart tools: discover talent, guide it to the right sport, and build training plans + professional classification.'}
          </p>

          {/* mini stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-8 max-w-3xl">
            {STATS.map((s, i) => {
              const Icon = s.icon;
              return (
                <div key={i} className="bg-white border border-border rounded-xl p-3 flex items-center gap-2 card-elevate" data-testid={`hub-stat-${i}`}>
                  <div className="w-9 h-9 rounded-lg grid place-items-center shrink-0" style={{ background: `hsla(${hue}, 70%, 35%, 0.12)`, color: `hsl(${hue}, 70%, 35%)` }}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div>
                    <div className="text-lg font-black leading-none">{s.value}</div>
                    <div className="text-[11px] text-muted-foreground">{isAr ? s.ar : s.en}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Tool cards */}
      <section className="px-4 pb-16">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-3 gap-5" data-testid="academy-tools-grid">
            {TOOLS.filter((t) => !(academy === 'special-olympics' && t.id === 'classify')).map((tool) => {
              const Icon = tool.icon;
              return (
                <Link key={tool.id} to={`${tool.to}?academy=${academy}`} data-testid={`hub-tool-${tool.id}`} className="block group">
                  <Card className="card-elevate h-full overflow-hidden border-2 hover:border-primary/30 transition-all">
                    <CardContent className="p-0">
                      <div className={`bg-gradient-to-br ${tool.accent} p-6 text-white`}>
                        <Icon className="w-9 h-9 mb-3" aria-hidden="true" />
                        <Badge variant="secondary" className="text-[10px] font-bold bg-white/25 text-white border-0 mb-2">
                          {isAr ? tool.badge_ar : tool.badge_en}
                        </Badge>
                        <h3 className="text-xl font-black leading-tight">{isAr ? tool.title_ar : tool.title_en}</h3>
                      </div>
                      <div className="p-5">
                        <p className="text-sm text-stone-700 leading-relaxed mb-4">{isAr ? tool.desc_ar : tool.desc_en}</p>
                        <div className="inline-flex items-center gap-2 text-sm font-bold text-primary group-hover:gap-3 transition-all">
                          {isAr ? 'ابدأ الآن' : 'Start now'}
                          <ArrowRight className={`w-4 h-4 ${isAr ? 'rotate-180' : ''}`} />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>

          {/* Back to academy landing */}
          <div className="mt-8 text-center">
            <Link to={`/academy/${academy}`} className="text-sm text-muted-foreground hover:text-primary inline-flex items-center gap-2" data-testid="hub-back-landing">
              <Search className="w-4 h-4" />
              {isAr ? 'عودة إلى صفحة الأكاديمية التعريفية' : 'Back to academy landing'}
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
